import { get, has } from 'lodash-es';

/**
 * Resolves schemaId parts against the schema's declared property names
 * rather than guessing from data. Property names may themselves contain
 * underscores, which the naive '_' split breaks apart, so at each level try
 * the longest join of leading parts that matches a declared property.
 * @param {Object} schema
 * @param {Array<string>} parts schemaId segments split on '_'
 * @returns {Array<string> | null} the real data path, or null if the parts
 * don't correspond to any schema path
 */
function getPathFromSchema(schema, parts) {
  if (!parts.length) {
    return [];
  }
  if (!schema || typeof schema !== 'object') {
    return null;
  }
  if (schema.type === 'array' || schema.items) {
    const [index, ...rest] = parts;
    if (!/^\d+$/.test(index)) {
      return null;
    }
    const itemSchema = Array.isArray(schema.items)
      ? schema.items[Number(index)] || schema.additionalItems
      : schema.items;
    const restPath = getPathFromSchema(itemSchema, rest);
    return restPath ? [index, ...restPath] : null;
  }
  const properties = schema.properties || {};
  for (let join = parts.length; join >= 1; join--) {
    const key = parts.slice(0, join).join('_');
    if (Object.prototype.hasOwnProperty.call(properties, key)) {
      const restPath = getPathFromSchema(properties[key], parts.slice(join));
      if (restPath) {
        return [key, ...restPath];
      }
    }
  }
  return null;
}

/**
 * @param {string} schemaId e.g. `'root_fullName_first'`
 * @param {Object | undefined} formData
 * @param {Object} [schema] page schema matching formData's shape; when
 * provided it is the authority on whether the schemaId names a real field
 * @returns corresponding value in formData (deeply nested), or
 * `'FORM_DATA_NOT_FOUND'` if the schemaId can't be resolved in formData
 */
export default function getFormDataFromSchemaId(schemaId, formData, schema) {
  if (!schemaId || !formData) {
    return undefined;
  }

  if (schemaId === 'root') {
    return formData;
  }

  const schemaIdParts = schemaId?.replace(/^root_/, '')?.split('_');
  if (has(formData, schemaIdParts)) {
    return get(formData, schemaIdParts);
  }

  // If the id resolves to a declared schema path, absent data just means the
  // field is empty — array builder items only contain keys from pages the
  // user has answered, and nested patterns (address, fullName) on later item
  // pages are missing their whole parent object. This also resolves
  // snake_case property names correctly, since the schema declares them.
  if (schema) {
    const schemaPath = getPathFromSchema(schema, schemaIdParts);
    if (schemaPath) {
      return get(formData, schemaPath);
    }
  }

  // A leaf key can be absent from data whose parent path still resolves —
  // array builder items only contain keys the user has filled in — and that
  // means the field is empty, not unresolvable. FORM_DATA_NOT_FOUND is
  // reserved for ids that can't be resolved at all, such as snake_case
  // property names that can't be split on '_'.
  const parentData =
    schemaIdParts.length > 1
      ? get(formData, schemaIdParts.slice(0, -1))
      : formData;

  return parentData && typeof parentData === 'object'
    ? undefined
    : 'FORM_DATA_NOT_FOUND';
}
